import { useAsyncData } from "../../hooks/useAsyncData";
import * as api from "../../lib/claimsApi.ts";
import type { AttachedInsight } from "../../types/claims";

/** One expert's insights, grouped for display. expertName is always
 *  present — rows without a name are filtered out before grouping. */
export interface GroupedExpertInsights {
  authorId: string;
  expertName: string;
  expertCredentials: string | null;
  expertBio: string | null;
  expertAvatarUrl: string | null;
  insights: Array<{ rank: number; title: string; body: string }>;
}

function groupByExpert(flat: AttachedInsight[]): GroupedExpertInsights[] {
  const map = new Map<string, GroupedExpertInsights>();
  for (const item of flat) {
    if (!item.contributorName) continue; // seeded without profile — skip
    const existing = map.get(item.authorId);
    if (existing) {
      existing.insights.push({ rank: item.rank, title: item.title, body: item.body });
    } else {
      map.set(item.authorId, {
        authorId: item.authorId,
        expertName: item.contributorName,
        expertCredentials: item.contributorCredentials,
        expertBio: item.contributorBio,
        expertAvatarUrl: item.contributorAvatarUrl,
        insights: [{ rank: item.rank, title: item.title, body: item.body }],
      });
    }
  }
  return Array.from(map.values());
}

interface UseInsightsResult {
  groupedInsights: GroupedExpertInsights[];
  isLoading: boolean;
}

/** Fetches expert insights attached to a verification and groups them by expert.
 *  Fires on mount via useAsyncData — userEventVerificationId is a URL param,
 *  always available on first render. Errors are swallowed silently — insights
 *  are contextual enrichment; their absence must not break the page. */
export function useInsights(userEventVerificationId: string | undefined): UseInsightsResult {
  const { data, loading } = useAsyncData(async () => {
    if (!userEventVerificationId) return [];
    try {
      const flat = await api.fetchInsights(userEventVerificationId);
      return groupByExpert(flat);
    } catch {
      return [];
    }
  });

  return { groupedInsights: data ?? [], isLoading: loading };
}
